import React, { FunctionComponent, memo, useEffect, useRef, useState } from 'react'
import { cn } from 'ethcss'
import { normalizeBackupCode, normalizeOtpCode } from 'core/models/SecondFactor'
import { otpCodeInputStyles as styles } from './OtpCodeInput-styles'

export interface OtpCodeInputProps {
    id?: string
    value: string
    length?: number
    mode?: 'digits' | 'alphanumeric'
    align?: 'start' | 'center'
    showSeparator?: boolean
    error?: boolean
    disabled?: boolean
    autoFocus?: boolean
    autoComplete?: string
    'aria-label'?: string
    onChange: (value: string) => void
    onComplete?: (value: string) => void
}

const normalizeValue = (raw: string, mode: 'digits' | 'alphanumeric', length: number) =>
    mode === 'alphanumeric' ? normalizeBackupCode(raw).slice(0, length) : normalizeOtpCode(raw, length)

const isTypedChar = (key: string, mode: 'digits' | 'alphanumeric') => {
    if (key.length !== 1) {
        return false
    }
    if (mode === 'digits') {
        return /\d/.test(key)
    }
    return /[a-zA-Z0-9]/.test(key)
}

const OtpCodeInputComponent: FunctionComponent<OtpCodeInputProps> = ({
    id = 'otp-code',
    value,
    length = 6,
    mode = 'digits',
    align = 'center',
    showSeparator = false,
    error,
    disabled,
    autoFocus,
    autoComplete = 'one-time-code',
    'aria-label': ariaLabel,
    onChange,
    onComplete,
}) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [focused, setFocused] = useState(false)
    const [caret, setCaret] = useState(0)
    const digits = normalizeValue(value, mode, length)
    const mid = Math.ceil(length / 2)
    const left = Array.from({ length: mid }, (_, index) => index)
    const right = Array.from({ length: length - mid }, (_, index) => index + mid)
    const activeCaret = Math.min(Math.max(caret, 0), length - 1)

    useEffect(() => {
        if (autoFocus) {
            inputRef.current?.focus()
        }
    }, [autoFocus])

    useEffect(() => {
        setCaret((prev) => Math.min(prev, Math.max(digits.length, 0), length - 1))
    }, [digits.length, length])

    const commit = (nextRaw: string, nextCaret: number) => {
        const next = normalizeValue(nextRaw, mode, length)
        onChange(next)
        setCaret(Math.min(Math.max(nextCaret, 0), Math.max(next.length, 0), length - 1))
        if (next.length === length) {
            onComplete?.(next)
        }
    }

    const focusInput = () => {
        inputRef.current?.focus()
    }

    const handleCellClick = (index: number) => (event: React.MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
        if (disabled) {
            return
        }
        focusInput()
        setCaret(Math.min(index, digits.length, length - 1))
    }

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault()
        if (disabled) {
            return
        }
        const pasted = event.clipboardData.getData('text') || ''
        const next = normalizeValue(pasted, mode, length)
        commit(next, next.length >= length ? length - 1 : next.length)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) {
            return
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault()
            setCaret((prev) => Math.max(0, prev - 1))
            return
        }

        if (event.key === 'ArrowRight') {
            event.preventDefault()
            setCaret((prev) => Math.min(length - 1, Math.min(digits.length, prev + 1)))
            return
        }

        if (event.key === 'Backspace') {
            event.preventDefault()
            if (digits[activeCaret]) {
                commit(digits.slice(0, activeCaret) + digits.slice(activeCaret + 1), activeCaret)
                return
            }
            if (activeCaret > 0) {
                const nextCaret = activeCaret - 1
                commit(digits.slice(0, nextCaret) + digits.slice(nextCaret + 1), nextCaret)
            }
            return
        }

        if (event.key === 'Delete') {
            event.preventDefault()
            if (digits[activeCaret]) {
                commit(digits.slice(0, activeCaret) + digits.slice(activeCaret + 1), activeCaret)
            }
            return
        }

        if (event.metaKey || event.ctrlKey || event.altKey) {
            return
        }

        if (!isTypedChar(event.key, mode)) {
            return
        }

        event.preventDefault()
        const char = mode === 'alphanumeric' ? event.key.toLowerCase() : event.key
        const next = `${digits.slice(0, activeCaret)}${char}${digits.slice(activeCaret + 1)}`.slice(0, length)
        commit(next, Math.min(activeCaret + 1, length - 1))
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const next = normalizeValue(event.target.value, mode, length)
        commit(next, next.length >= length ? length - 1 : next.length)
    }

    const renderCell = (index: number) => (
        <span
            key={index}
            className={cn(
                styles.cell,
                !!digits[index] && !error && styles.cellFilled,
                focused && index === activeCaret && !error && styles.cellCurrent,
                error && styles.cellError
            )}
            onMouseDown={handleCellClick(index)}
        >
            {digits[index] || ''}
        </span>
    )

    return (
        <div
            className={cn(
                styles.root,
                align === 'start' ? styles.alignStart : styles.alignCenter,
                focused && styles.focus,
                error && styles.error
            )}
            onClick={focusInput}
        >
            <input
                ref={inputRef}
                id={id}
                className={styles.input}
                type="text"
                inputMode={mode === 'alphanumeric' ? 'text' : 'numeric'}
                autoComplete={autoComplete}
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={digits}
                disabled={disabled}
                aria-label={ariaLabel}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
            />
            <div className={cn(styles.cells, mode === 'alphanumeric' && styles.cellsAlphanumeric)} aria-hidden="true">
                <div className={styles.group}>{left.map(renderCell)}</div>
                {showSeparator ? <span className={styles.separator}>-</span> : null}
                <div className={styles.group}>{right.map(renderCell)}</div>
            </div>
        </div>
    )
}

export const OtpCodeInput = memo(OtpCodeInputComponent)
